import React, { useEffect, useMemo, useState } from 'react';
import { Snowflake } from 'lucide-react';
import JanelaModal from '../../JanelaModal';
import { estoqueParaTe } from '../../../data/embryos';
import {
  comAHasteInteira, hastesPorTamanho, textoDasHastes, enderecoDoBotijao,
} from '../../../data/estoque';

/**
 * Escolher, no botijão, o que vai ser descongelado para esta TE.
 *
 * O caso que trouxe isto: a FIV rendeu pouco e o laboratório completou as
 * receptoras desvitrificando embriões antigos. Eles são de outro serviço, não
 * apareciam na TE, e o que não está na TE não é cobrado.
 *
 * O botijão é o do laboratório todo, não só o deste cliente: genética se
 * vende, e o VT do Bruno Brito foi transferido na TE da Fazenda Santa Rita.
 * Abre no cliente do serviço; os outros estão no seletor.
 *
 * A haste vai inteira: não se descongela um embrião só de uma palheta com
 * três. Por isso marcar um marca a haste toda, e o topo diz quantas hastes há
 * de cada tamanho — "quero descongelar três" se responde ali.
 *
 * Nada é gravado aqui. Os escolhidos entram na lista da TE e só saem do
 * estoque quando a transferência é aprovada e a TE é salva.
 */
// Valor do seletor para o embrião sem cliente: '' já é "todos".
const SEM_CLIENTE = 'sem-cliente';

const DescongeladosDoEstoque = ({ service, laboratoryId, jaNaTe = [], onEscolher, onFechar }) => {
  const [linhas, setLinhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [sel, setSel] = useState(new Set());
  // '' = todos os clientes.
  const [cliente, setCliente] = useState(service?.client_id || '');

  useEffect(() => {
    let cancelado = false;
    estoqueParaTe(service, laboratoryId)
      .then(l => {
        if (cancelado) return;
        const livres = l.filter(e => !jaNaTe.includes(e.id));
        setLinhas(livres);
        // Cliente sem nada no botijão: abrir vazio só faria procurar o seletor.
        if (!livres.some(e => e.client_id === service?.client_id)) setCliente('');
      })
      .catch(e => { if (!cancelado) setErro(e.message); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id, laboratoryId]);

  const clientes = useMemo(() => {
    const m = new Map();
    linhas.forEach(l => {
      const c = m.get(l.client_id) || { id: l.client_id, nome: l.client_name || 'Sem cliente', n: 0 };
      c.n += 1;
      m.set(l.client_id, c);
    });
    return [...m.values()];
  }, [linhas]);

  const visiveis = useMemo(
    () => (cliente ? linhas.filter(l => (l.client_id || SEM_CLIENTE) === cliente) : linhas),
    [linhas, cliente]);

  const hastes = useMemo(() => textoDasHastes(hastesPorTamanho(
    visiveis.map(l => ({ ...l, metodo: l.freeze_method })))), [visiveis]);

  const deOutroCliente = (l) => l.client_id !== service?.client_id;

  const alternar = (id) => setSel(s => {
    const daHaste = comAHasteInteira(new Set([id]), linhas);
    const n = new Set(s);
    const tirar = n.has(id);
    daHaste.forEach(x => (tirar ? n.delete(x) : n.add(x)));
    return n;
  });

  const tamanhoDaHaste = (h) => linhas.filter(l => l.haste === h).length;

  return (
    <JanelaModal chave="descongelados-do-estoque" camada="z-[60]" largura="sm:max-w-3xl"
      Icone={Snowflake}
      titulo="Descongelar do estoque"
      subtitulo={<>
        {service?.client_name ? `${service.client_name} · ` : ''}
        Embriões congelados de outros serviços, deste ou de outro cliente. Entram na TE e são cobrados nela como os outros.
      </>}
      onFechar={onFechar}>
      <div className="flex-1 overflow-y-auto p-4">
        {carregando && <p className="text-sm text-slate-500 py-6 text-center">Abrindo o botijão…</p>}
        {erro && (
          <div className="mb-3 text-sm text-slate-700 bg-slate-50 border border-slate-300 rounded-lg px-3 py-2">{erro}</div>
        )}
        {!carregando && !erro && linhas.length === 0 && (
          <p className="text-sm text-slate-500 py-6 text-center">
            Não há embrião congelado de outro serviço no botijão.
          </p>
        )}

        {clientes.length > 0 && (
          <label className="mb-3 flex items-center gap-2 text-xs text-slate-600">
            Cliente
            <select value={cliente} onChange={e => setCliente(e.target.value)}
              className="border border-slate-300 rounded-lg px-2 py-1 text-sm text-slate-700">
              {service?.client_id && !clientes.some(c => c.id === service.client_id) && (
                <option value={service.client_id}>{service.client_name || 'Este cliente'} (0)</option>
              )}
              {clientes.map(c => (
                <option key={c.id || SEM_CLIENTE} value={c.id || SEM_CLIENTE}>
                  {c.nome}{c.id === service?.client_id ? ' — este serviço' : ''} ({c.n})
                </option>
              ))}
              <option value="">Todos os clientes ({linhas.length})</option>
            </select>
          </label>
        )}

        {!carregando && linhas.length > 0 && visiveis.length === 0 && (
          <p className="text-sm text-slate-500 py-6 text-center">
            Este cliente não tem embrião congelado de outro serviço. Os dos outros clientes estão no seletor acima.
          </p>
        )}

        {hastes && (
          <p className="mb-3 text-xs text-slate-600">VT: {hastes}</p>
        )}

        {visiveis.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-slate-500 border-b border-slate-200 text-xs">
              <tr>
                <th className="w-8"></th>
                <th className="px-2 py-1.5 text-left font-medium">Haste</th>
                <th className="px-2 py-1.5 text-left font-medium">Nº</th>
                <th className="px-2 py-1.5 text-left font-medium">Doadora × Touro</th>
                <th className="px-2 py-1.5 text-center font-medium">Fase</th>
                <th className="px-2 py-1.5 text-left font-medium">Método</th>
                <th className="px-2 py-1.5 text-left font-medium">Onde está</th>
                <th className="px-2 py-1.5 text-left font-medium">Serviço</th>
                {!cliente && <th className="px-2 py-1.5 text-left font-medium">Cliente</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {visiveis.map(l => (
                <tr key={l.id} onClick={() => alternar(l.id)}
                  className={`cursor-pointer ${sel.has(l.id) ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                  <td className="pl-2">
                    <input type="checkbox" readOnly checked={sel.has(l.id)} className="rounded border-slate-300" />
                  </td>
                  <td className="px-2 py-1.5 text-slate-700">
                    {l.haste
                      ? <>H{l.haste} <span className="text-xs text-slate-400">({tamanhoDaHaste(l.haste)})</span></>
                      : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-slate-700">{l.service_number ?? '—'}</td>
                  <td className="px-2 py-1.5 text-slate-700">
                    {l.donor_identifier || '—'}
                    <span className="text-slate-400"> × {l.bull_name || '—'}</span>
                  </td>
                  <td className="px-2 py-1.5 text-center text-slate-600">{l.stage || '—'}</td>
                  <td className="px-2 py-1.5 text-slate-600">
                    {l.freeze_method === 'Direct Transfer' ? 'DT' : l.freeze_method === 'Vitrificação' ? 'Vitri' : (l.freeze_method || '—')}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 text-xs">{enderecoDoBotijao(l) || '—'}</td>
                  <td className="px-2 py-1.5 text-slate-500 text-xs">
                    {l.servico_numero ? `#${l.servico_numero}` : l.de_fora ? 'de fora' : '—'}
                  </td>
                  {!cliente && (
                    <td className={`px-2 py-1.5 text-xs ${deOutroCliente(l) ? 'text-slate-700' : 'text-slate-400'}`}>
                      {l.client_name || '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex gap-2 p-4 border-t">
        <button
          onClick={() => { onEscolher(linhas.filter(l => sel.has(l.id))); onFechar(); }}
          disabled={sel.size === 0}
          className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {sel.size === 0 ? 'Escolha os embriões' : `Levar ${sel.size} para a TE`}
        </button>
        <button onClick={onFechar}
          className="px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50">
          Cancelar
        </button>
      </div>
    </JanelaModal>
  );
};

export default DescongeladosDoEstoque;
